import * as React from 'react';
import type { ColumnDef, CellContext } from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  AuditLogDataDifference,
  TreeNode,
  ConfigEntityDetail,
  AnnotatedJsonSchema,
} from '@web-config-app/core';
import { Box, UtilityText, VisuallyHidden } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { getBreadcrumbsForProperty } from '@web-config-app/core-react';
import { TableLoadingState } from '../table-loading-state/table-loading-state.component';
import { TableEmptyState } from '../table-empty-state/table-empty-state.component';

export type AuditLogDataDiffProps = {
  data: AuditLogDataDifference[];
  isLoading?: boolean;
  entityVersion: string;
  previousEntityVersion?: string;
  /**
   * A precomputed map from JSON path → TreeNode. Used by `getBreadcrumbsForProperty`
   * to produce human-readable breadcrumb labels for paths. This is a fallback
   * strategy when the schema-based label resolution doesn't find a label.
   */
  entityTreeNodeMap?: Map<string, TreeNode>;
  /**
   * The full JSON Schema for the entity, annotated with `x-entity-label` extensions.
   * This is the PRIMARY source used to resolve human-readable labels for each path
   * segment (e.g. turning "benefitPlanId" → "Benefit Plan ID").
   */
  schemaForAuditLogLabels?: AnnotatedJsonSchema;
  fromEntity?: ConfigEntityDetail;
  toEntity?: ConfigEntityDetail;
};

/**
 * Renders a diff table comparing two entity versions. Each row = one changed field.
 *
 * The core challenge is turning raw JSON paths (e.g. "domainEntityAttributes.benefits.0.name")
 * into readable labels ("Benefits → Item 1 → Name") using three strategies:
 *   1. Walk JSON schema for `x-entity-label` annotations (most precise)
 *   2. Use `getBreadcrumbsForProperty` with entity tree node map (fallback)
 *   3. Format raw path segments as title case (last resort)
 */
export function AuditLogDataDiff({
  data,
  isLoading,
  entityVersion,
  previousEntityVersion,
  entityTreeNodeMap,
  schemaForAuditLogLabels,
  fromEntity,
  toEntity,
}: AuditLogDataDiffProps) {
  const { formatMessage } = useIntl();

  // Strip "domainEntityAttributes." prefix so paths match the schema.
  // e.g. "domainEntityAttributes.benefits.0.name" → "benefits.0.name"
  const normalizeAuditPath = React.useCallback((path: string) => {
    if (path.startsWith('domainEntityAttributes.translations.')) {
      return path.replace(
        /^domainEntityAttributes\.translations\./,
        'translations.',
      );
    }
    if (path.startsWith('domainEntityAttributes.')) {
      return path.replace(/^domainEntityAttributes\./, '');
    }
    return path;
  }, []);

  // Clean bracket-notation locale keys: `["en"]` or `['en']` → `en`
  const normalizeLocaleSegment = React.useCallback(
    (segment: string) =>
      // strip leading [" and trailing "]
      segment.replace(/^\[['"]?/, '').replace(/['"]?\]$/, ''),
    [],
  );

  // Last-resort formatter: converts raw path to readable text.
  // Numeric segments → "Item N", camelCase → spaced, snake_case → Title Case, joined with " → "
  const formatPathFallback = React.useCallback((path: string) => {
    if (path === 'entityMetadata.name') {
      return 'Entity Name';
    }
    return path
      .split('.')
      .map((segment) => {
        if (/^\d+$/.test(segment)) {
          // "0" → "Item 1" (convert to 1-indexed)
          return `Item ${parseInt(segment, 10) + 1}`;
        }
        return segment
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (str) => str.toUpperCase())
          .trim()
          .split('_')
          .map(
            (word) =>
              word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
          )
          .join(' ');
      })
      .join(' → ');
  }, []);

  const getSchemaNodeLabel = React.useCallback(
    (schemaNode?: AnnotatedJsonSchema) => {
      const xEntityLabel = schemaNode?.['x-entity-label'];
      if (!xEntityLabel) {
        return undefined;
      }
      const labelFromKey = xEntityLabel.key
        ? formatMessage({ id: xEntityLabel.key })
        : undefined;
      return xEntityLabel.text ?? labelFromKey;
    },
    [formatMessage],
  );

  // Walk one level deeper into the schema for a given path segment.
  // Handles: array index → items, property → properties[seg], dynamic key → additionalProperties,
  // discriminated oneOf → match by discriminator value, non-discriminated oneOf → match by property name.
  const resolveSchemaNodeForSegment = React.useCallback(
    (
      schemaNode: AnnotatedJsonSchema | undefined,
      segment: string,
      segmentData?: unknown,
    ) => {
      if (!schemaNode) {
        return undefined;
      }

      // Check if this segment is a numeric array index (e.g. "0", "1", "2")
      // try to parse as number
      const maybeIndex = parseInt(segment, 10);
      // "0" → true, "name" → false
      const isArrayIndex =
        !Number.isNaN(maybeIndex) && String(maybeIndex) === segment;
      if (isArrayIndex) {
        // For array indices, the child schema is the array's `items` schema
        return schemaNode.items as AnnotatedJsonSchema | undefined;
      }

      // Direct property lookup — the most common case
      if (schemaNode.properties?.[segment]) {
        return schemaNode.properties[segment];
      }

      // additionalProperties handles map/dictionary-style objects where keys
      // are dynamic and not explicitly listed in `properties`
      if (
        schemaNode.additionalProperties &&
        typeof schemaNode.additionalProperties === 'object'
      ) {
        return schemaNode.additionalProperties as AnnotatedJsonSchema;
      }

      // oneOf (polymorphic/union types) — try to match the correct variant
      if (schemaNode.oneOf) {
        const oneOfSchemas = schemaNode.oneOf as AnnotatedJsonSchema[];
        const discriminatorProperty = schemaNode.discriminator?.propertyName;

        // If there's a discriminator AND we have actual data to check against,
        // use the discriminator value to pick the right variant
        if (
          discriminatorProperty &&
          segmentData &&
          typeof segmentData === 'object'
        ) {
          const discriminatorValue = (segmentData as Record<string, unknown>)[
            discriminatorProperty
          ];
          if (discriminatorValue != null) {
            // find the oneOf variant whose discriminator enum includes the actual value
            const match = oneOfSchemas.find((v) =>
              v.properties?.[discriminatorProperty]?.enum?.includes(
                discriminatorValue,
              ),
            );
            // return the segment's schema within that variant
            if (match) return match.properties?.[segment] ?? match;
          }
        }

        // Fallback: just find whichever oneOf variant has a property matching
        // the segment name. Less precise but works when we don't have data.
        const matchingOneOf = oneOfSchemas.find((variant) =>
          Boolean(variant.properties?.[segment]),
        );
        // return segment schema or the whole variant
        return matchingOneOf?.properties?.[segment] ?? matchingOneOf;
      }

      return undefined;
    },
    [],
  );

  // Try to get a meaningful label for array items (e.g. "Benefit" instead of "Item").
  // Checks x-entity-label on the items schema, then tries oneOf variants via next segment.
  const getArrayItemLabelFromSchema = React.useCallback(
    (
      schemaNode: AnnotatedJsonSchema | undefined,
      nextSegment: string | undefined,
    ) => {
      if (!schemaNode) {
        return undefined;
      }

      const directLabel = getSchemaNodeLabel(schemaNode);
      if (directLabel) {
        return directLabel;
      }

      // If the array items are a oneOf, peek at the next segment to figure
      // out which variant we're in and use its label
      if (nextSegment && schemaNode.oneOf && Array.isArray(schemaNode.oneOf)) {
        const oneOfSchemas = schemaNode.oneOf as AnnotatedJsonSchema[];
        const matchingOneOf = oneOfSchemas.find((oneOfSchema) =>
          Boolean(oneOfSchema?.properties?.[nextSegment]),
        );

        return getSchemaNodeLabel(matchingOneOf);
      }

      return undefined;
    },
    [getSchemaNodeLabel],
  );

  // PRIMARY label resolution: walks schema + entity data in parallel, segment by segment,
  // building a breadcrumb trail. Data walk is needed for discriminated oneOf resolution.
  const resolveLabelFromFullSchema = React.useCallback(
    (path: string, rowData?: AuditLogDataDifference) => {
      if (!schemaForAuditLogLabels) {
        return undefined;
      }

      // strip domainEntityAttributes prefix
      const normalizedPath = normalizeAuditPath(path);

      let segments = normalizedPath.split('.').filter(Boolean);
      if (segments.length === 0) {
        return undefined;
      }

      // Pick which entity version's data to use for the parallel data walk.
      // If this row is a deletion (changedTo is undefined), prefer fromEntity
      // since that's where the data existed. Otherwise prefer toEntity.
      const entityAttributes =
        rowData?.changedTo === undefined
          ? fromEntity?.attributes ?? toEntity?.attributes
          : toEntity?.attributes ?? fromEntity?.attributes;

      let currentSchemaNode: AnnotatedJsonSchema | undefined =
        schemaForAuditLogLabels;
      const labels: string[] = [];
      let currentData: unknown = entityAttributes?.domainEntityAttributes;

      // Special handling for translation paths: extract the locale and adjust
      // the data pointer to the locale-specific domainEntityAttributes
      if (segments[0] === 'translations' && segments[1]) {
        const localeKey = normalizeLocaleSegment(segments[1]);
        labels.push(`Translation (${localeKey})`);
        const localeData = entityAttributes?.translations;
        // pivot data pointer into the locale-specific attributes
        currentData =
          localeData?.[localeKey as keyof typeof localeData]
            ?.domainEntityAttributes;
        // Skip past the "translations" and locale segments since we've handled them
        segments = segments.slice(2);
      }
      // Also skip a redundant domainEntityAttributes segment if present
      if (segments[0] === 'domainEntityAttributes') {
        segments = segments.slice(1);
      }

      // Walk each remaining segment, resolving schema nodes and building labels
      for (let index = 0; index < segments.length; index += 1) {
        const segment = segments[index];
        const nextNode = resolveSchemaNodeForSegment(
          currentSchemaNode,
          segment,
          currentData,
        );
        if (!nextNode) {
          // If we can't resolve the schema node, stop — we'll return whatever
          // labels we've collected so far (partial breadcrumb is better than nothing)
          break;
        }

        // Advance the data pointer to stay in sync with the schema walk
        if (currentData != null && typeof currentData === 'object') {
          currentData = (currentData as Record<string, unknown>)[segment];
        } else {
          currentData = undefined;
        }

        const maybeIndex = parseInt(segment, 10);
        const isArrayIndex =
          !Number.isNaN(maybeIndex) && String(maybeIndex) === segment;
        if (isArrayIndex) {
          // For array items, try to get a label from the schema (e.g. "Benefit")
          // and display as "Benefit (1)" instead of just "Item 1"
          const nextPathSegment = segments[index + 1];
          const arrayItemLabel =
            getArrayItemLabelFromSchema(nextNode, nextPathSegment) ?? 'Item';
          labels.push(`${arrayItemLabel} (${maybeIndex + 1})`);
        } else {
          const schemaLabel = getSchemaNodeLabel(nextNode);
          labels.push(schemaLabel ?? formatPathFallback(segment));
        }

        currentSchemaNode = nextNode;
      }

      if (labels.length > 0) {
        return labels.join(' → ');
      }

      return undefined;
    },
    [
      schemaForAuditLogLabels,
      formatPathFallback,
      fromEntity,
      getArrayItemLabelFromSchema,
      getSchemaNodeLabel,
      normalizeLocaleSegment,
      normalizeAuditPath,
      resolveSchemaNodeForSegment,
      toEntity,
    ],
  );

  // Fallback #2: use getBreadcrumbsForProperty with entity tree node map.
  const getLabelFromEntity = React.useCallback(
    (path: string, entity?: ConfigEntityDetail) => {
      if (!entityTreeNodeMap || !entity) {
        return undefined;
      }

      const breadcrumbs = getBreadcrumbsForProperty({
        path,
        data: entity.attributes,
        pathPrefix: undefined,
        entityTreeNodeMap,
        translate: formatMessage,
      });

      if (breadcrumbs.length > 0) {
        return breadcrumbs.map(({ label }) => label).join(' → ');
      }

      return undefined;
    },
    [entityTreeNodeMap, formatMessage],
  );

  // Tries all three label strategies in order: schema → tree node map → raw path formatting.
  // Picks which entity version to try first based on whether the row is an add/delete/modify.
  const resolveAffectedObjectLabel = React.useCallback(
    (path: string, rowData: AuditLogDataDifference) => {
      const fullSchemaLabel = resolveLabelFromFullSchema(path, rowData);
      if (fullSchemaLabel) {
        return fullSchemaLabel;
      }

      if (entityTreeNodeMap && (fromEntity || toEntity)) {
        let resolvedLabel: string | undefined;
        if (rowData.changedTo === undefined) {
          // Deletion: prefer fromEntity (the data existed there)
          resolvedLabel =
            getLabelFromEntity(path, fromEntity) ??
            getLabelFromEntity(path, toEntity);
        } else if (rowData.changedFrom === undefined) {
          // Addition: prefer toEntity (the data exists there)
          resolvedLabel =
            getLabelFromEntity(path, toEntity) ??
            getLabelFromEntity(path, fromEntity);
        } else {
          // Modification: prefer toEntity (most current)
          resolvedLabel =
            getLabelFromEntity(path, toEntity) ??
            getLabelFromEntity(path, fromEntity);
        }
        if (resolvedLabel) return resolvedLabel;
      }

      return formatPathFallback(path);
    },
    [
      entityTreeNodeMap,
      formatPathFallback,
      fromEntity,
      getLabelFromEntity,
      resolveLabelFromFullSchema,
      toEntity,
    ],
  );

  const hasAnyChangedFromValues = React.useMemo(
    () =>
      data.some(
        (item) =>
          item.changedFrom !== undefined &&
          item.changedFrom !== null &&
          item.changedFrom !== '',
      ),
    [data],
  );

  const columns: ColumnDef<AuditLogDataDifference>[] = React.useMemo(
    () => [
      {
        accessorKey: 'affectedObject',
        header: formatMessage({ id: 'AUDIT_LOG_AFFECTED_OBJECT' }),
        cell: (info) => {
          const path = info.getValue() as string;
          const rowData = info.row.original;

          return resolveAffectedObjectLabel(path, rowData);
        },
      },
      // Conditionally include the "Changed From" column only when there are actual form values to show
      ...(hasAnyChangedFromValues
        ? [
            {
              accessorKey: 'changedFrom' as const,
              header: formatMessage({ id: 'AUDIT_LOG_CHANGED_FROM' }),
              cell: (info: CellContext<AuditLogDataDifference, unknown>) => {
                const value = info.getValue();
                return value !== null && value !== undefined
                  ? String(value)
                  : '';
              },
            },
          ]
        : []),
      {
        accessorKey: 'changedTo',
        header: formatMessage({ id: 'AUDIT_LOG_CHANGED_TO' }),
        cell: (info) => {
          const value = info.getValue();
          return value !== null && value !== undefined ? String(value) : '';
        },
      },
    ],
    [formatMessage, hasAnyChangedFromValues, resolveAffectedObjectLabel],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const renderTableBody = () => {
    if (isLoading) {
      return (
        <TableLoadingState
          colSpan={columns.length}
          marginBottom="half"
          testId="SkeletonDiffGroup"
        />
      );
    }

    if (data.length === 0) {
      return (
        <TableEmptyState
          colSpan={columns.length}
          message={formatMessage({ id: 'AUDIT_LOG_NO_CHANGES' })}
        />
      );
    }

    return table.getRowModel().rows.map((row) => (
      <Box
        as="tr"
        key={row.id}
        css={{
          paddingY: '$one',
          marginBlockEnd: '$half', // Add space between rows
          // Each row gets full border radius like a card
          '& td:first-child': {
            borderTopLeftRadius: '$medium',
            borderBottomLeftRadius: '$medium',
          },
          '& td:last-child': {
            borderTopRightRadius: '$medium',
            borderBottomRightRadius: '$medium',
          },
        }}
      >
        {row.getVisibleCells().map((cell, index, cells) => {
          const isFirstCell = index === 0;
          const isLastCell = index === cells.length - 1;

          return (
            <Box
              as="td"
              key={cell.id}
              scope={isFirstCell ? 'row' : undefined}
              css={{
                padding: '$one',
                verticalAlign: 'top',
                border: '1px solid $onSurfaceBorderSubdued',
                borderInlineStart: isFirstCell
                  ? '1px solid $onSurfaceBorderSubdued'
                  : 'none',
                borderInlineEnd: isLastCell
                  ? '1px solid $onSurfaceBorderSubdued'
                  : 'none',
                borderBlockStart: '1px solid $onSurfaceBorderSubdued',
                borderBlockEnd: '1px solid $onSurfaceBorderSubdued',
              }}
            >
              <UtilityText size="sm">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </UtilityText>
            </Box>
          );
        })}
      </Box>
    ));
  };

  const tableCaption = previousEntityVersion
    ? formatMessage(
        { id: 'AUDIT_LOG_CHANGE_DETAILS_FOR' },
        { entityVersion, previousEntityVersion },
      )
    : formatMessage({ id: 'AUDIT_LOG_CHANGES_TO_VERSION' }, { entityVersion });

  return (
    <Box
      as="table"
      css={{
        width: '100%',
        // Use separate borders instead of collapsed to allow border-radius to work on table cells
        // This requires manual border management on individual cells but enables rounded corners
        borderCollapse: 'separate',
        borderSpacing: '0 $half', // Add vertical spacing between rows
      }}
    >
      <VisuallyHidden as="caption">{tableCaption}</VisuallyHidden>
      <Box as="thead">
        {table.getHeaderGroups().map((headerGroup) => (
          <Box
            as="tr"
            key={headerGroup.id}
            css={{
              backgroundColor: '$surfaceBackgroundSecondary',
            }}
          >
            {headerGroup.headers.map((header) => (
              <Box
                as="th"
                key={header.id}
                css={{
                  padding: '$one',
                  textAlign: 'left',
                }}
                scope="col"
              >
                {header.isPlaceholder ? null : (
                  <UtilityText emphasis="emphasized" size="sm">
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </UtilityText>
                )}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
      <Box
        as="tbody"
        css={{
          backgroundColor: '$surfaceBackgroundPrimary',
        }}
      >
        {renderTableBody()}
      </Box>
    </Box>
  );
}
